import { FusionSuggestionRequestOptions } from "../../models/search/fusion/fusion-suggestion-request-options.model";
import { ProductSuggestion } from "../../models/search/suggestions/product-suggestion.model";
import { QuickLinkSuggestion } from "../../models/search/suggestions/quick-link-suggestion.model";
import { SearchSuggestion } from "../../models/search/suggestions/search-suggestion.model";
import { FusionSearchSuggestionHttpService } from "./fusion/fusion-search-suggestion-http.service";

export interface Suggestions {
  suggestions: Array<SearchSuggestion>;
  quickLinkSuggestions: Array<QuickLinkSuggestion>;
  productSuggestions: Array<ProductSuggestion>;
}

interface BusinessRulesByType {
  "custom-link": Array<{ href: string; text: string; appendix?: any }>;
  product: Array<{
    href: string;
    text: string;
    img: string;
    installment_price?: number;
    retail_price: number;
  }>;
}

interface FusionSuggestion {
  term: string;
  weight: number;
  payload: string;
}

const minSearchTermLengthForBackendCall = 3;
export class SearchSuggestionService {
  private static instance: SearchSuggestionService;
  private cache: Map<string, Suggestions> = new Map();
  private fusionSearchSuggestionHttpService: FusionSearchSuggestionHttpService;

  private constructor() {
    // Private constructor -> singleton
    this.fusionSearchSuggestionHttpService =
      FusionSearchSuggestionHttpService.getInstance();
  }

  static getInstance(): SearchSuggestionService {
    if (!SearchSuggestionService.instance) {
      SearchSuggestionService.instance = new SearchSuggestionService();
    }
    return SearchSuggestionService.instance;
  }

  static parseBusinessRules(json: any): BusinessRulesByType {
    const businessRules: Array<FusionSuggestion> =
      (json.fusion && json.fusion.business_rules) || [];
    return businessRules
      .sort((a, b) => b.weight - a.weight)
      .map(businessRule => {
        try {
          return JSON.parse(businessRule.payload);
        } catch (err) {
          return null;
        }
      })

      .filter(parsedBusinessRule => !!parsedBusinessRule)
      .reduce((accumulator, current) => {
        if (current.type && current.payload) {
          accumulator[current.type] = [
            ...(accumulator[current.type] || []),
            current.payload,
          ];
        }

        return accumulator;
      }, {});
  }

  private createWidgetsFromBusinessRules(json: any): {
    quickLinkSuggestions: Array<QuickLinkSuggestion>;
    productSuggestions: Array<ProductSuggestion>;
  } {
    const businessRulesMap: Partial<BusinessRulesByType> =
      SearchSuggestionService.parseBusinessRules(json);

    return {
      quickLinkSuggestions: (businessRulesMap["custom-link"] || [])
        .map(({ href, text, appendix }) => {
          return { href, text, appendix } as QuickLinkSuggestion;
        })
        // remove duplicates by href
        .filter(
          (item, pos, a) =>
            a.map(mapItem => mapItem["href"]).indexOf(item["href"]) === pos,
        ),
      productSuggestions: (businessRulesMap["product"] || [])
        .map(({ img, href, installment_price, retail_price, text }) => {
          return {
            img,
            href,
            text,
            installmentPrice: installment_price,
            retailPrice: retail_price,
          } as ProductSuggestion;
        }) // remove duplicates by href
        .filter(
          (item, pos, a) =>
            a.map(mapItem => mapItem["href"]).indexOf(item["href"]) === pos,
        ),
    };
  }

  private transformFusionResponse(json: any = {}): Suggestions {
    const suggestions: Array<FusionSuggestion> =
      (json.fusion && json.fusion.swc_combined_suggestions) || [];
    const { quickLinkSuggestions, productSuggestions } =
      this.createWidgetsFromBusinessRules(json);

    return {
      quickLinkSuggestions,
      productSuggestions,
      suggestions: suggestions.map(suggestion => {
        return {
          term: suggestion.term,
        } as SearchSuggestion;
      }),
    };
  }

  /**
   * Fetches suggestions from backend and transforms it
   */
  fetchSuggestions(
    searchTerm: string,
    options: FusionSuggestionRequestOptions,
    isUserLoggedIn: boolean,
  ): Promise<Suggestions> {
    if (searchTerm.length < minSearchTermLengthForBackendCall) {
      // avoid performing a backend call if searchTerm isn't long enough
      return Promise.resolve({
        suggestions: [],
        productSuggestions: [],
        quickLinkSuggestions: [],
      });
    }

    if (!this.cache.has(searchTerm)) {
      // load from network, atm fusion is the only supported search provider
      return this.fusionSearchSuggestionHttpService
        .get(searchTerm, options, isUserLoggedIn)
        .then((json: any) => {
          const suggestions: Suggestions = this.transformFusionResponse(json);

          this.cache.set(searchTerm, suggestions);

          return suggestions;
        });
    } else {
      // load from cache
      return Promise.resolve(this.cache.get(searchTerm)!);
    }
  }
}
